import type { CSSProperties } from 'react'
import { colors } from 'styles/variables'
import { getReadableTextColor, isHexColor } from 'utils/colorUtils'

const TAG_BACKGROUND_TINT_PERCENT = 15
const TAG_TEXT_SHADE_PERCENT = 45

type PriorityColorScheme = {
  bg: string
  text: string
  border: string
  hex: string
}

function getPriorityStyleColor(priorityLevel: number): PriorityColorScheme {
  const range = Math.floor(priorityLevel / 10)

  switch (range) {
    case 1: // 10-19: Base setting
      return { bg: 'bg-danger-1', text: 'text-danger-6', border: 'border-danger-2', hex: colors.danger3 }
    case 2: // 20-29: Planned deviation
      return { bg: 'bg-info-1', text: 'text-info-6', border: 'border-info-2', hex: colors.info3 }
    case 3: // 30-39: Unplanned deviation
      return { bg: 'bg-warning-1', text: 'text-warning-6', border: 'border-warning-3', hex: colors.warning4 }
    case 4: // 40-49
      return { bg: 'bg-grey-2', text: 'text-grey-7', border: 'border-grey-4', hex: colors.grey4 }
    case 5: // 50-59: Follow-up
      return { bg: 'bg-success-1', text: 'text-success-6', border: 'border-success-3', hex: colors.success3 }
    case 6: // 60-69: Settlements
      return { bg: 'bg-[#E9DDF6]', text: 'text-[#4B1D68]', border: 'border-[#B58AD4]', hex: '#B58AD4' }
    case 7: // 70-79
      return { bg: 'bg-grey-2', text: 'text-grey-7', border: 'border-grey-4', hex: colors.grey4 }
    case 8: // 80-88
      return { bg: 'bg-grey-2', text: 'text-grey-7', border: 'border-grey-4', hex: colors.grey4 }
    case 9: // 90-99
      return { bg: 'bg-grey-2', text: 'text-grey-7', border: 'border-grey-4', hex: colors.grey4 }
    default:
      return { bg: 'bg-grey-2', text: 'text-grey-7', border: 'border-grey-4', hex: colors.grey4 }
  }
}

export function getPriorityBarColors(
  priorityLevel: number,
  displayColor?: string | null
): { background: string; text?: string } {
  if (isHexColor(displayColor)) {
    return { background: displayColor, text: getReadableTextColor(displayColor) }
  }
  return { background: getPriorityStyleColor(priorityLevel).hex }
}

export function getPriorityTagAppearance(
  priorityLevel: number,
  displayColor?: string | null
): { className: string; style?: CSSProperties } {
  if (isHexColor(displayColor)) {
    return {
      className: 'border',
      style: {
        backgroundColor: `color-mix(in srgb, ${displayColor} ${TAG_BACKGROUND_TINT_PERCENT}%, white)`,
        color: `color-mix(in srgb, ${displayColor} ${TAG_TEXT_SHADE_PERCENT}%, black)`,
        borderColor: displayColor,
      },
    }
  }
  const range = getPriorityStyleColor(priorityLevel)
  return { className: `${range.bg} ${range.text} border ${range.border}` }
}
